import type { HotelIngredientFilters } from '@apps/main/modules/recipe/ingredients';
import { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { DEFAULT_AVAILABILITY_ORDER_SUGGESTION } from '@apps/main/modules/recipe/ingredients/hotelIngredient';

import type {
  HeartedHotel,
  StarsRatingFilter,
  FullStars,
} from '@libs/trip-planner-interfaces';
import {
  HOTEL_TYPES,
  OLD_POPULAR_FILTERS,
  AccommodationType,
  AmenitiesFilter,
  ExperienceFilter,
  AccommodationRule,
  BoardBasisFilter,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import { assertValue, isDefined } from '@libs/utils';
import {
  trackDeserializeHotelWithOldIngredientPrefix,
  trackDeserializeHotelWithOldRoomPrefix,
  trackDeserializeHotelWithoutSuggestionOrderPrefix,
  trackDeserializeHotelWithoutBoardBasisPrefix,
  trackDeserializeHotelWithoutHotelFiltersPrefix,
  trackDeserializeHotelWithoutRoomTypeId,
} from '@libs/feature-toggle';

import {
  isSerializedActivity,
  ActivitySerializer,
} from '../../activitiySerializer';
import {
  DESTINATION_PRODUCTS_PREFIX,
  DESTINATION_PRODUCTS_SEPARATOR,
  CONSTRAINT_TEXT_SUFFIX,
} from '../constants';
import type { DeserializedStayConstraintText } from '../deserializeStay';
import {
  deserializeNumberOfNights,
  deserializeStayConstraintText,
  deserializeTransportType,
} from '../deserializeStay';

import {
  OLD_DESTINATION_PRODUCTS_PREFIX,
  HOTEL_FILTERS_PREFIX,
  NEW_SELECTED_ROOMS_PREFIX,
  OLD_SELECTED_ROOMS_PREFIX,
  STAY_REFUNDABILITY_PREFIX,
  ROOM_PROP_PREFIX,
  HOTEL_NAME_PREFIX,
  HOTEL_INGREDIENT_SUFFIX,
  SUGGESTION_ORDER_PREFIX,
  SerializedAvailabilityOrder,
} from './constants';

export function deserializeHotel(value: string): HotelIngredient {
  const {
    constraintText,
    destinationProducts,
    numberOfNights,
    filters,
    transportType,
    availabilityOrder,
  } = deserializeIngredientParameters(value);

  const { displayText, searchTerm, filterText } =
    deserializeConstraintText(constraintText);

  const result = new HotelIngredient(searchTerm);
  if (displayText) {
    result.displayText = displayText;
  }

  destinationProducts.forEach((product) => {
    if (isSerializedActivity(product)) {
      result.subIngredients.push(
        ActivitySerializer.deserialize(product, result.ingredientId)
      );
    } else {
      result.heartedHotels.push(deserializeHeartedHotel(product));
    }
  });

  result.numberOfNights = deserializeNumberOfNights(numberOfNights);

  result.filters = deserializeFilters(filters);
  if (filterText) {
    result.filters.filterText = filterText;
  }

  result.transportTypeToBeReached = deserializeTransportType(transportType);

  result.availabilityOrder = deserializeAvailabilityOrder(availabilityOrder);

  return result;
}

export interface SerializedHotelParameters {
  constraintText: string;
  destinationProducts: string[];
  numberOfNights: string;
  filters: string;
  transportType: string;
  availabilityOrder: string;
}

export function deserializeIngredientParameters(
  value: string
): SerializedHotelParameters {
  const sanitizedValue = decodeURIComponent(value.replace(/\+/g, ' ')).replace(
    HOTEL_INGREDIENT_SUFFIX,
    ''
  );

  let indexOfDestinationProducts: number;

  const destinationProductsPrefixIndex = sanitizedValue.lastIndexOf(
    DESTINATION_PRODUCTS_PREFIX
  );

  if (destinationProductsPrefixIndex !== -1) {
    indexOfDestinationProducts = destinationProductsPrefixIndex;
  } else {
    indexOfDestinationProducts = sanitizedValue.lastIndexOf(
      OLD_DESTINATION_PRODUCTS_PREFIX
    );
  }

  const constraintText = sanitizedValue.substring(
    0,
    indexOfDestinationProducts
  );

  const hotelCodesAndProps = sanitizedValue.substring(
    indexOfDestinationProducts +
      (destinationProductsPrefixIndex !== -1
        ? DESTINATION_PRODUCTS_PREFIX.length
        : OLD_DESTINATION_PRODUCTS_PREFIX.length)
  );

  const serializedPropsLengthWithoutFilters = 8;
  let propsLength: number;
  let indexOfProps: number;

  if (hotelCodesAndProps.includes(HOTEL_FILTERS_PREFIX)) {
    indexOfProps = hotelCodesAndProps.indexOf(HOTEL_FILTERS_PREFIX);
  } else {
    trackDeserializeHotelWithoutHotelFiltersPrefix();
    propsLength =
      serializedPropsLengthWithoutFilters +
      HOTEL_TYPES.length +
      OLD_POPULAR_FILTERS.length;

    indexOfProps = hotelCodesAndProps.length - propsLength;
  }

  const destinationProducts = hotelCodesAndProps
    .substring(0, indexOfProps)
    .split(DESTINATION_PRODUCTS_SEPARATOR)
    .filter((product) => !!product);

  if (destinationProducts.length && destinationProductsPrefixIndex === -1) {
    trackDeserializeHotelWithOldIngredientPrefix();
  }

  const fullProps = hotelCodesAndProps.includes(HOTEL_FILTERS_PREFIX)
    ? hotelCodesAndProps.substring(indexOfProps + HOTEL_FILTERS_PREFIX.length)
    : hotelCodesAndProps.substring(indexOfProps);

  const hasAvailabilityOrder = fullProps.includes(SUGGESTION_ORDER_PREFIX);

  if (!hasAvailabilityOrder) {
    trackDeserializeHotelWithoutSuggestionOrderPrefix();
  }

  const serializedTransportTypeLength = 2;
  const serializedAvailabilityOrderLength = 1;
  const transportTypePropsStartIndex = hasAvailabilityOrder
    ? -serializedTransportTypeLength -
      SUGGESTION_ORDER_PREFIX.length -
      serializedAvailabilityOrderLength
    : -serializedTransportTypeLength;

  const props = fullProps.slice(0, transportTypePropsStartIndex);
  const numberOfNights = props.slice(0, 2);
  const filters = props.slice(2);

  const transportType = fullProps.slice(
    transportTypePropsStartIndex,
    fullProps.length +
      transportTypePropsStartIndex +
      serializedTransportTypeLength
  );

  const availabilityOrder = hasAvailabilityOrder
    ? fullProps.slice(
        transportTypePropsStartIndex + serializedTransportTypeLength
      )
    : '';

  return {
    constraintText,
    destinationProducts,
    numberOfNights,
    filters,
    transportType,
    availabilityOrder,
  };
}

export function deserializeConstraintText(
  constraintText: string
): DeserializedStayConstraintText & {
  filterText?: string;
} {
  let filterText: string | undefined = undefined;

  if (constraintText.length === 0) {
    throw new Error('Invalid serialized constraint text.');
  }

  const filterTextRegExp = new RegExp(
    `(${HOTEL_NAME_PREFIX}(.*?)${CONSTRAINT_TEXT_SUFFIX}).*$`
  );

  const filterTextMatch = constraintText.match(filterTextRegExp);

  if (filterTextMatch) {
    const matchedFilterText = filterTextMatch[2];

    assertValue(
      matchedFilterText,
      'hotelSerializer.ts > deserializeConstraintText > matchedFilterText'
    );

    filterText = matchedFilterText;
  }

  return { ...deserializeStayConstraintText(constraintText), filterText };
}

export function deserializeHeartedHotel(
  serializedHeartedHotel: string
): HeartedHotel {
  let codeAndRefundability: string | undefined;
  let rooms: string[];
  if (serializedHeartedHotel.includes(NEW_SELECTED_ROOMS_PREFIX)) {
    [codeAndRefundability, ...rooms] = serializedHeartedHotel.split(
      NEW_SELECTED_ROOMS_PREFIX
    );
  } else {
    [codeAndRefundability, ...rooms] = serializedHeartedHotel.split(
      OLD_SELECTED_ROOMS_PREFIX
    );

    if (rooms.length) {
      trackDeserializeHotelWithOldRoomPrefix();
    }
  }

  assertValue(
    codeAndRefundability,
    'hotelSerializer.ts > HotelSerializer > deserialize > codeAndRefundability'
  );

  const [code, isRefundable] = codeAndRefundability.split(
    STAY_REFUNDABILITY_PREFIX
  );

  assertValue(
    code,
    'hotelSerializer.ts > HotelSerializer > deserialize > code'
  );

  const hearted: HeartedHotel = {
    code,
    isRefundable: !!isRefundable,
  };

  if (rooms.length) {
    const onlyHasRoomCodes = !rooms.some((room) =>
      room.includes(ROOM_PROP_PREFIX)
    );

    if (onlyHasRoomCodes) {
      trackDeserializeHotelWithoutBoardBasisPrefix();
      hearted.roomCodes = rooms;
    } else {
      hearted.rooms = rooms.map((room) => {
        const [roomCode, boardBasis, roomTypeId] = room.split(ROOM_PROP_PREFIX);
        assertValue(
          roomCode,
          'hotelSerializer.ts > HotelSerializer > deserialize > roomCode'
        );
        assertValue(
          boardBasis,
          'hotelSerializer.ts > HotelSerializer > deserialize > boardBasis'
        );

        if (!isDefined(roomTypeId)) {
          trackDeserializeHotelWithoutRoomTypeId();
        }

        let sanitizedCode: string;
        try {
          sanitizedCode = decodeURIComponent(roomCode);
        } catch {
          sanitizedCode = roomCode;
        }

        let sanitizedBoardBasis: string;
        try {
          sanitizedBoardBasis = decodeURIComponent(boardBasis);
        } catch {
          sanitizedBoardBasis = boardBasis;
        }

        //TODO Move this function to libs utils
        function parseStringToNumber(value?: string): number | undefined {
          const valueAsNumber = Number(value);
          return value !== '' && !isNaN(valueAsNumber)
            ? valueAsNumber
            : undefined;
        }

        const sanitizedRoomTypeId = parseStringToNumber(roomTypeId);

        return {
          code: sanitizedCode,
          boardBasis: sanitizedBoardBasis,
          roomTypeId: sanitizedRoomTypeId,
        };
      });
    }
  }

  return hearted;
}

export function deserializeFilters(
  serializedFilters: string
): HotelIngredientFilters {
  const result: HotelIngredientFilters = HotelIngredient.defaultFilters;

  const serializedStarRating = serializedFilters.slice(0, 1);

  result.starRating = deserializeStarRating(serializedStarRating);
  result.carefullySelectedOnly = serializedFilters[1] === '1';
  if (serializedFilters[2] === '1') {
    result.selectedHotelTypes.push(AccommodationType.Hotel);
  }
  if (serializedFilters[3] === '1') {
    result.selectedHotelTypes.push(AccommodationType.Apartment);
  }
  if (serializedFilters[4] === '1') {
    result.selectedHotelTypes.push(AccommodationType.HouseOrCottage);
  }
  if (serializedFilters[5] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.Pool);
  }
  if (serializedFilters[6] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.Parking);
  }
  if (serializedFilters[7] === '1') {
    result.selectedExperienceFilters.push(ExperienceFilter.PetsAllowed);
  }

  if (serializedFilters[8] === '1') {
    result.selectedHotelRules.push(AccommodationRule.FreeCancellation);
  }
  if (serializedFilters[9] === '1') {
    result.boardBasis.push(BoardBasisFilter.RoomOnly);
  }
  if (serializedFilters[10] === '1') {
    result.boardBasis.push(BoardBasisFilter.Breakfast);
  }
  if (serializedFilters[11] === '1') {
    result.boardBasis.push(BoardBasisFilter.HalfBoard);
  }
  if (serializedFilters[12] === '1') {
    result.boardBasis.push(BoardBasisFilter.FullBoard);
  }
  if (serializedFilters[13] === '1') {
    result.boardBasis.push(BoardBasisFilter.AllInclusive);
  }
  if (serializedFilters[14] === '1') {
    result.selectedExperienceFilters.push(ExperienceFilter.Business);
  }
  if (serializedFilters[15] === '1') {
    result.selectedExperienceFilters.push(ExperienceFilter.Family);
  }
  if (serializedFilters[16] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.Spa);
  }
  if (serializedFilters[17] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.Gym);
  }
  if (serializedFilters[18] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.Wifi);
  }
  if (serializedFilters[19] === '1') {
    result.selectedAmenitiesFilters.push(AmenitiesFilter.AirConditioned);
  }
  if (serializedFilters[20] === '1') {
    result.selectedAmenitiesFilters.push(
      AmenitiesFilter.VehicleChargingStation
    );
  }

  return result;
}

export function deserializeStarRating(value: string): StarsRatingFilter {
  const number = parseInt(value, 10);

  if (isNaN(number) || number < 0) {
    return 0;
  }

  /* eslint-disable @typescript-eslint/no-magic-numbers */
  if (number > 5) {
    return 5;
  }
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  return number as FullStars;
}

export function deserializeAvailabilityOrder(value: string): AvailabilityOrder {
  const serializedAvailabilityOrder = value.replace(
    SUGGESTION_ORDER_PREFIX,
    ''
  );

  switch (serializedAvailabilityOrder) {
    case SerializedAvailabilityOrder.None:
      return AvailabilityOrder.None;

    case SerializedAvailabilityOrder.Cheapest:
      return AvailabilityOrder.Cheapest;

    case SerializedAvailabilityOrder.Popular:
      return AvailabilityOrder.Popular;

    default:
      return DEFAULT_AVAILABILITY_ORDER_SUGGESTION;
  }
}
